import { CheckboxGroupFormComponent } from "components/Form/components/CheckboxGroup/CheckboxGroupFormComponent";
import { Form } from "components/Form/components/Form/Form";
import { SwitchFormComponent } from "components/Form/components/Switch/SwitchFormComponent";
import { TextFormComponent } from "components/Form/components/Text/TextFormComponent";
import { Popover, popoverPosition } from "components/Popover/Popover";
import {
    CheckboxTriggerButton,
    FilterChipContainer,
} from "containers/AdvancedSearch/components/CheckboxTriggerButton";
import { getBundles } from "containers/Bundles/Bundles.selectors";
import { CustomFilterField } from "containers/ExecutionFilter/CustomFilter/CustomFilterField";
import {
    addCustomFilterMutator,
    validateCustomFilter,
} from "containers/ExecutionFilter/CustomFilter/utils";
import { DateTimeFilter } from "containers/ExecutionFilter/DateTimeFilter";
import { LocationFilter } from "containers/ExecutionFilter/LocationFilter";
import { StatusFilter } from "containers/ExecutionFilter/StatusFilter";
import { getProjects } from "containers/Projects/Projects.selectors";
import { Bundle, Project } from "graphql/generated/graphql";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { Field, Form as FinalForm, FormSpy } from "react-final-form";
import { useSelector } from "react-redux";
import { sortAlphabeticallyByProperty } from "utils/sorting";
import "./ExecutionFilter.scss";
import {
    endedStatusArray,
    executionTypes,
    getStatusChipLabel,
    notEndedStatusArray,
    statusList,
    totalStatusesCount,
} from "./constants";
import { ExecutionFilterProps } from "./interfaces";
import { onCheckboxValueChange, submitFilterForm } from "./utils";
import { useMatchExecutionFilterLocation } from "utils/RouterManager/utils";

const ExecutionFilter = ({
    hasParentExecutionId,
    setFilterOnMount,
    filteredValues,
    setQueryString,
    setInitialFilterHandler,
    hasStoredFilter,
    isFilterInitialized,
    queryParams,
    customFilterValues,
    setFilterFormRef,
    matchingFields,
    filteredFieldList,
    initialFilter,
    filtersPristine,
    setFiltersPristine,
}: ExecutionFilterProps) => {
    const { executionFilterLocation } = useMatchExecutionFilterLocation();

    const unsortedProjectList: Project[] = useSelector(getProjects);
    const unsortedBundleList: Bundle[] = useSelector(getBundles);

    const sortByName = <T,>(list: T[]) => sortAlphabeticallyByProperty<T>(list, "name");

    const projectList = useMemo(() => sortByName(unsortedProjectList), [unsortedProjectList]);
    const bundleList = useMemo(() => sortByName(unsortedBundleList), [unsortedBundleList]);

    const filterSelectedStatuses = useCallback((arrayToInclude, currentFilter) => {
        const selectedStatuses = currentFilter
            .filter((status) => arrayToInclude.includes(status))
            .map(getStatusChipLabel);
        const hasAllValues = selectedStatuses.length === arrayToInclude.length;
        return { selectedStatuses, hasAllValues };
    }, []);

    useEffect(() => {
        if (!isFilterInitialized) {
            return;
        }
        if (!hasStoredFilter || setFilterOnMount) {
            setInitialFilterHandler("replace");
        }
    }, [hasStoredFilter, isFilterInitialized, setFilterOnMount, setInitialFilterHandler]);

    const getStatusText = useCallback(
        (statusFilter: string[]) => {
            if (statusFilter.length === totalStatusesCount) {
                return "all";
            }
            if (!statusFilter.length) {
                return "none";
            }

            let currentFilter: string[] = [];

            const selectedNotEndedStatuses = filterSelectedStatuses(
                notEndedStatusArray,
                statusFilter
            );
            const selectedEndedStatuses = filterSelectedStatuses(endedStatusArray, statusFilter);

            if (selectedNotEndedStatuses.hasAllValues && !selectedEndedStatuses.hasAllValues) {
                currentFilter = ["Not ended", ...selectedEndedStatuses.selectedStatuses];
            } else if (
                !selectedNotEndedStatuses.hasAllValues &&
                selectedEndedStatuses.hasAllValues
            ) {
                currentFilter = ["Ended", ...selectedNotEndedStatuses.selectedStatuses];
            } else {
                const mappedFilteredStatus = statusFilter.map(getStatusChipLabel);
                currentFilter = [...mappedFilteredStatus];
            }

            return <FilterChipContainer currentFilter={currentFilter} />;
        },
        [filterSelectedStatuses]
    );

    const initialValues = useMemo(() => {
        return {
            ...filteredValues,
            customFilter: customFilterValues,
            _hasNestedValidationErrors: false,
        };
    }, [filteredValues, customFilterValues]);

    const CustomFormTag: keyof JSX.IntrinsicElements = `${hasParentExecutionId ? "div" : "form"}`;
    const hasLocationField = useMemo(
        () =>
            !hasParentExecutionId &&
            executionFilterLocation !== "project" &&
            executionFilterLocation !== "bundle",
        [executionFilterLocation, hasParentExecutionId]
    );
    return (
        <FinalForm
            keepDirtyOnReinitialize
            onSubmit={(formData) => {
                const allValues = { ...formData };
                delete allValues._hasNestedValidationErrors;
                return submitFilterForm({
                    prevValues: filteredValues,
                    newValues: allValues,
                    setQueryString,
                    textFields: ["name"],
                    initialLocationValue: initialFilter.location,
                });
            }}
            initialValues={initialValues}
            mutators={{
                addCustomFilter: addCustomFilterMutator,
            }}
            validate={(values) => {
                const errors: Record<string, any> = {};
                validateCustomFilter(values, errors);
                return errors;
            }}
        >
            {({ handleSubmit, values, form }) => {
                setFilterFormRef(form);
                return (
                    <>
                        <CustomFormTag
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (!filtersPristine) {
                                    handleSubmit();
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !filtersPristine) {
                                    e.preventDefault();
                                    handleSubmit();
                                }
                            }}
                            tabIndex={0}
                        >
                            <div className="execution-search-filters">
                                <Form.Column>
                                    <Field
                                        name="name"
                                        id="name"
                                        fieldAttr={{
                                            placeholder: "Search...",
                                        }}
                                        component={TextFormComponent}
                                    />
                                </Form.Column>
                                {hasLocationField && (
                                    <Form.Column>
                                        <LocationFilter
                                            projectList={projectList}
                                            bundleList={bundleList}
                                            currentProjectValue={values.location.projects}
                                            currentBundleValue={values.location.bundles}
                                            currentWorkspaceValue={values.location.workspace}
                                            form={form}
                                            onProjectChange={(newValue) => (e) =>
                                                onCheckboxValueChange({
                                                    form,
                                                    identifier: "location.projects",
                                                    currentValue: values.location.projects,
                                                    newValue,
                                                })(e)}
                                            onBundleChange={(newValue) => (e) =>
                                                onCheckboxValueChange({
                                                    form,
                                                    identifier: "location.bundles",
                                                    currentValue: values.location.bundles,
                                                    newValue,
                                                })(e)}
                                        />
                                    </Form.Column>
                                )}

                                <Form.Column>
                                    <Popover
                                        trigger={
                                            <CheckboxTriggerButton
                                                label="Status"
                                                currentFilter={values.status || []}
                                                totalItemCount={totalStatusesCount}
                                                customFilterText={getStatusText(values.status)}
                                            />
                                        }
                                        closeOnInsideClick={false}
                                        closeOnEnter={true}
                                        contentClassName={"checkbox-group-filter"}
                                        {...popoverPosition}
                                    >
                                        <Field name="status" id="status">
                                            {({ input }) => (
                                                <StatusFilter
                                                    input={input}
                                                    endedStatusList={statusList.ended}
                                                    notEndedStatusList={statusList.notEnded}
                                                    onStatusFilterChange={(newValues) => {
                                                        form.change("status", newValues);
                                                    }}
                                                    onChange={(newValue) => (e) =>
                                                        onCheckboxValueChange({
                                                            form,
                                                            identifier: "status",
                                                            currentValue: values.status,
                                                            newValue,
                                                        })(e)}
                                                />
                                            )}
                                        </Field>
                                    </Popover>
                                </Form.Column>
                                <Form.Column verticalAlignment>
                                <DateTimeFilter name="created_at_gt" label="After" />
                                </Form.Column>
                                <Form.Column verticalAlignment>
                                <DateTimeFilter name="created_at_lt" label="Before" />
                                </Form.Column>
                                <Form.Column>
                                    <Popover
                                        trigger={
                                            <CheckboxTriggerButton
                                                label="Execution type"
                                                currentFilter={values.execution_type}
                                                totalItemCount={executionTypes.length}
                                            />
                                        }
                                        closeOnInsideClick={false}
                                        closeOnEnter={true}
                                        {...popoverPosition}
                                    >
                                        <Field
                                            name="execution_type"
                                            id="execution_type"
                                            component={CheckboxGroupFormComponent}
                                            fieldProps={{
                                                options: executionTypes.map((type) => ({
                                                    value: type,
                                                    label: type,
                                                })),
                                                currentValue: values.execution_type,
                                                onChange: (newValue) => (e) => {
                                                    onCheckboxValueChange({
                                                        form,
                                                        identifier: "execution_type",
                                                        currentValue: values.execution_type,
                                                        newValue,
                                                    })(e);
                                                },
                                            }}
                                        />
                                    </Popover>
                                </Form.Column>
                                {!hasParentExecutionId && (
                                    <Form.Column verticalAlignment>
                                        <div className="baseSizeLabel">
                                            <Field
                                                name="no_parent_id"
                                                id="no_parent_id"
                                                component={SwitchFormComponent}
                                                fieldProps={{
                                                    label: "Top-level executions only",
                                                }}
                                                fieldAttr={{
                                                    size: "md",
                                                }}
                                            />
                                        </div>
                                    </Form.Column>
                                )}
                            </div>
                            <CustomFilterField
                                form={form}
                                matchingFields={matchingFields}
                                filteredFieldList={filteredFieldList}
                                values={values}
                                clearAllFilters={() => {
                                    const valuesToRestart = {
                                        ...initialFilter,
                                        _hasNestedValidationErrors: false,
                                    };
                                    form.restart(valuesToRestart);
                                    handleSubmit();
                                }}
                            />
                        </CustomFormTag>

                        <FormSpy
                            onChange={({ values, initialValues }) => {
                                const isPristine = isEqual(values, initialValues);
                                setFiltersPristine(isPristine);
                            }}
                        />
                    </>
                );
            }}
        </FinalForm>
    );
};

export default ExecutionFilter;
